import { ArrowRight, Factory, Globe2 } from "lucide-react";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import worldMapUrl from "@/assets/world-map.svg";

const FREIGHT_MODES = ["Air", "Sea", "Road"] as const;

// Long S-curves swept left→right give the route field a wave/route feel rather
// than a single bent line. Dash-animated via .worldwide-routes (reduced-motion gated).
const ROUTES = [
  "M-40 110 C 260 40 460 170 760 90 S 1180 60 1340 140",
  "M-40 210 C 240 150 520 250 780 180 S 1160 150 1340 220",
  "M-40 300 C 300 250 480 330 820 270 S 1180 250 1340 300",
  "M-40 60 C 320 20 560 120 900 60 S 1200 40 1340 80",
  "M-40 350 C 280 320 540 380 860 340 S 1180 330 1340 360",
] as const;

// Hub markers, as % of the world map's box. Hand-placed on the live map (see git history
// for the drag-calibration tool), then rendered in a layer that shares the map's exact
// geometry, so they stay pinned to the coastlines when the window resizes. Order is cosmetic.
const HUBS = [
  { x: 83.2, y: 40.0 },
  { x: 80.7, y: 41.5 },
  { x: 68.2, y: 31.3 },
  { x: 56.0, y: 29.3 },
  { x: 52.3, y: 30.3 },
  { x: 54.1, y: 36.2 },
  { x: 53.6, y: 23.4 },
  { x: 48.1, y: 35.3 },
  { x: 18.6, y: 23.5 },
  { x: 34.2, y: 66.3 },
] as const;

// Shared geometry: the map fill and the marker layer use the exact same box so % marker
// coordinates land on the same point of the map at every viewport width. Shifted up
// (-45%) so the dense northern marker cluster stays visible in the compact band height.
const MAP_BOX =
  "absolute left-1/2 top-1/2 aspect-[95/62] w-[min(1500px,135%)] -translate-x-1/2 -translate-y-[45%]";

function MapBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* Faded, glowing world map. Gentle edge vignette keeps land visible under all hubs. */}
      <div
        className={MAP_BOX}
        style={{
          WebkitMaskImage:
            "radial-gradient(ellipse 88% 90% at 50% 48%, black 52%, transparent 95%)",
          maskImage: "radial-gradient(ellipse 88% 90% at 50% 48%, black 52%, transparent 95%)",
        }}
      >
        <div
          className="worldwide-map h-full w-full opacity-[0.24]"
          style={{
            WebkitMaskImage: `url(${worldMapUrl})`,
            maskImage: `url(${worldMapUrl})`,
          }}
        />
      </div>

      {/* Animated wave routes (decorative, not geo-anchored) */}
      <svg
        viewBox="0 0 1300 400"
        preserveAspectRatio="xMidYMid slice"
        className="worldwide-routes absolute inset-0 h-full w-full"
      >
        {ROUTES.map((d, index) => (
          <path key={d} d={d} className={index > 1 ? "hidden sm:block" : undefined} />
        ))}
      </svg>

      {/* Hub markers — same box as the map, so they stay pinned to the coastlines */}
      <div className={MAP_BOX}>
        {HUBS.map((h, i) => (
          <span
            key={i}
            className="absolute h-2 w-2"
            style={{ left: `${h.x}%`, top: `${h.y}%`, transform: "translate(-50%, -50%)" }}
          >
            <span
              className="worldwide-node absolute inset-0 rounded-full bg-primary/60"
              style={{ animationDelay: `${i * 0.45}s` }}
            />
            <span className="absolute inset-0 rounded-full bg-primary shadow-[0_0_10px_2px] shadow-primary/70" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function WorldwideDelivery() {
  const { messages, site, href } = useSite();
  const logisticsHref = localizeUrl("/solutions/logistics", site.locale, href);

  const stats = [{ value: messages.worldwideStat1Value, label: messages.worldwideStat1Label }];

  return (
    <section className="worldwide-band relative isolate flex min-h-[440px] items-center overflow-hidden border-y border-white/10 py-14 text-white">
      <MapBackdrop />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
          {/* Message */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs text-white/80 backdrop-blur">
              <Globe2 className="h-3 w-3 text-primary" />
              {messages.worldwideEyebrow}
              <span className="mx-0.5 h-3 w-px bg-white/20" />
              <Factory className="h-3 w-3 text-primary" />
              <span className="font-medium text-white">{messages.worldwideOriginLabel}</span>
            </div>

            <h2 className="mt-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
              {messages.worldwideHeadline}
            </h2>
            <p className="mt-2 max-w-md text-pretty text-sm text-white/65">
              {messages.worldwideSub}
            </p>

            <a
              href={logisticsHref}
              className="group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-white underline decoration-white/30 underline-offset-2 transition-colors hover:text-white/80 hover:decoration-white/50"
            >
              {messages.worldwideCta}
              <ArrowRight className="h-3.5 w-3.5 text-primary transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>

          {/* Inline stats */}
          <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-4 sm:gap-x-8">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                  {s.value}
                </div>
                <div className="mt-0.5 text-xs text-white/55">{s.label}</div>
              </div>
            ))}
            <div>
              <div className="flex flex-wrap gap-1.5">
                {FREIGHT_MODES.map((mode) => (
                  <span
                    key={mode}
                    className="rounded-md bg-primary/25 px-2 py-0.5 text-xs font-semibold text-white ring-1 ring-inset ring-primary/40"
                  >
                    {mode}
                  </span>
                ))}
              </div>
              <div className="mt-1.5 text-xs text-white/55">{messages.worldwideStat3Label}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
